/* eslint-disable @typescript-eslint/no-unused-vars */ // 45 - 47
import React, { createContext, useRef, useState } from 'react';
import { cloneDeep } from 'lodash';
import { v4 as generateUUID } from 'uuid';

export type Model = { name: string };
export type Message = { name: string };
export type MessageOrMessageName = Message | string;
export type Effect = () => any;

export type ReducerResult = {
  model?: Model;
  effect?: Effect; // Beware of stale closures!
  discardMessage?: boolean;
};

/**
 *  @param message - the message to be processed
 *  @param model - the model to be processed (optional)
 *
 *  @returns ReducerResult with updated model and optionally a side effect, OR discardMessage set to true
 *    if message is supported and model is defined in parameter and supported by reducer.
 *  @returns ReducerResult with side effect
 *    if message parameter is supported by reducer, and model parameter is undefined.
 **/
export type ReducerFunction = (message: any, model?: any) => ReducerResult;

export type Reducer = {
  apply: ReducerFunction;
  applicableMessageNames: Array<string>;
  applicableModelNames?: Array<string>;
};

type ReducerWrapper = {
  id: string;
  reducer: Reducer;
};

interface ModelMap {
  [name: string]: Model;
}

const defaultValue = {
  models: {},
  dispatch: (message: MessageOrMessageName) => {},
  registerModel: (model: Model) => () => {},
  registerReducer: (reducer: Reducer) => () => {},
};

export const ReducerContext = createContext(defaultValue);

export function ReducerContextProvider({ children }) {
  const [modelsUseState, setModelsUseState] = useState<ModelMap>({});
  const modelsRef = useRef<ModelMap>({});
  const setModels = (newValue) => {
    modelsRef.current = newValue;
    setModelsUseState(newValue);
  };

  const reducers = useRef<Array<ReducerWrapper>>([]);

  const shouldApplyReducer = (reducer: Reducer, message: Message, model?: Model) => {
    const isMessageApplicable = reducer.applicableMessageNames.includes(message.name);

    const isModelApplicable =
      typeof model === 'undefined' ||
      typeof reducer.applicableModelNames === 'undefined' ||
      reducer.applicableModelNames.includes(model.name);

    return isMessageApplicable && isModelApplicable;
  };

  const isReducerApplicableToModels = (reducer: Reducer) =>
    reducer.applicableModelNames !== undefined;

  const getMessageFromName = (messageName: string): Message => {
    return {
      name: messageName,
    };
  };

  const dispatch = (messageOrMessageName: MessageOrMessageName) => {
    let modelsCurrent: ModelMap = cloneDeep(modelsRef.current);
    let currentEffects: Array<Effect> = [];
    let messageDiscarded = false;
    const message =
      typeof messageOrMessageName === 'string'
        ? getMessageFromName(messageOrMessageName)
        : messageOrMessageName;

    const applyReducer = (message: Message, reducer: Reducer, model?: Model): boolean => {
      const reducerResult = reducer.apply(message, model);

      if (reducerResult === undefined) {
        return true;
      }

      if (reducerResult.discardMessage !== undefined && reducerResult.discardMessage) {
        messageDiscarded = true;
        return false;
      }

      if (model !== undefined && reducerResult.model !== undefined) {
        modelsCurrent[model.name.toLowerCase()] = reducerResult.model;
      }

      if (reducerResult.effect !== undefined) {
        currentEffects.push(reducerResult.effect);
      }

      return true;
    };

    reducers.current.every(({ reducer }) => {
      if (isReducerApplicableToModels(reducer)) {
        Object.keys(modelsCurrent).map((modelName) => {
          const model = modelsCurrent[modelName];
          if (shouldApplyReducer(reducer, message, model)) {
            return applyReducer(message, reducer, model);
          }

          return true;
        });
      } else if (shouldApplyReducer(reducer, message)) {
        return applyReducer(message, reducer);
      }

      return true;
    });

    if (!messageDiscarded) {
      setModels(modelsCurrent);
      for (const effect of currentEffects) {
        effect();
      }
    }
  };

  const registerModel = (model: Model) => {
    const modelName = model.name.toLowerCase();

    if (modelsRef.current[modelName]) {
      throw new Error(`Reducer context: model ${model} already registered`);
    }

    setModels({
      ...modelsRef.current,
      [modelName]: model,
    });

    return () => {
      const modelsNew = {
        ...modelsRef.current,
      };

      delete modelsNew[modelName];
      setModels(modelsNew);
    };
  };

  const registerReducer = (reducer: Reducer) => {
    const newId = generateUUID();
    reducers.current = [
      ...reducers.current,
      {
        id: newId,
        reducer: reducer,
      },
    ];

    return () => {
      reducers.current = reducers.current.filter((oldReducer) => {
        const result = oldReducer.id !== newId;
        return result;
      });
    };
  };

  return (
    <ReducerContext.Provider
      value={{ dispatch, registerReducer, registerModel, models: modelsUseState }}
    >
      {children}
    </ReducerContext.Provider>
  );
}
